import {
  Checkbox,
  CommandButton,
  IconButton,
  Separator,
  Stack,
  Text,
  mergeStyleSets,
} from '@fluentui/react';
import React from 'react';
import { isMobile } from 'react-device-detect';
import AppReducer from '../../shared/state/AppReducer';
import Store from '../../shared/state/Store';
import UiReducer from '../../shared/state/UiReducer';
import HelpButton from '../../shared/help/HelpButton';
import TextRendererHelper from '../../shared/util/TextRendererHelper';
import StackTokens from '../../shared/StackTokens';
import Theme from '../../shared/Theme';
import LinkButton from '../../shared/LinkButton';
import AppsApi from '../../shared/util/AppsApi';
import {
  dataSourceIconSrc,
  DATA_SOURCE_ICON_MENU_PX,
} from '../../shared/util/DataSourceIcon';
import ObservationSourceDialog, {
  buildObservationSource,
  createObservationSourceId,
} from './ObservationSourceDialog';

const componentStyles = mergeStyleSets({
  sourceListRow: {
    minHeight: '32px',
    padding: '2px 6px',
    marginLeft: '-6px',
    marginRight: '-6px',
    borderRadius: '2px',
    selectors: {
      ':hover': {
        backgroundColor: Theme.Colors.Gray,
      },
    },
  },

  sourceRowActions: {
    flexShrink: 0,
    marginRight: '4px',
  },

  sourceIcon: {
    display: 'block',
    flexShrink: 0,
    objectFit: 'contain',
  },
});

const reorderIconButtonStyles = {
  root: { backgroundColor: 'transparent' },
  rootHovered: { backgroundColor: 'transparent' },
  rootPressed: { backgroundColor: 'transparent' },
  rootDisabled: { backgroundColor: 'transparent' },
  icon: { fontSize: '10px' },
  iconDisabled: { fontSize: '10px' },
};

const normalizeObservationSources = (sources) => (
  (sources || []).map((source) => buildObservationSource(source))
);

export default function DataSources() {
  const [globalState, dispatch] = Store.useStore();
  const uuid = UiReducer.getGenericUiAttribute(globalState, UiReducer.Keys.CloudAppManagerAppSelected);
  const cloudApps = UiReducer.getGenericUiAttribute(globalState, UiReducer.ListOptions.cloudManagerAppsList) || [];
  const app = cloudApps.filter((a) => a.uuid === uuid)[0];
  const [sourceDialog, setSourceDialog] = React.useState(null);

  const externalSources = app?.externalSources || {};
  const externalSourcesEnabled = !!externalSources.enabled;
  const activeObservationSources = normalizeObservationSources(externalSources.observationSources);

  React.useEffect(() => {
    setSourceDialog(null);
  }, [uuid]);

  const update = async (configPatch) => {
    const jResponse = await AppsApi.saveApp(configPatch, uuid);

    if (jResponse.code >= 300) {
      dispatch(UiReducer.uiSetErrors(jResponse.messages));
      return false;
    }

    if (uuid === AppReducer.getAppData(globalState)?.uuid) {
      dispatch(AppReducer.setAppData(jResponse));
    }

    dispatch(UiReducer.setGenericUiListItem(UiReducer.ListOptions.cloudManagerAppsList, jResponse, (a) => a.uuid === uuid));
    return true;
  };

  const saveExternalSources = async (observationSources, enabled = externalSourcesEnabled) => update({
    externalSources: {
      ...externalSources,
      enabled,
      observationSources: normalizeObservationSources(observationSources),
    },
  });

  const removeObservationSource = async (sourceIndex) => {
    await saveExternalSources(activeObservationSources.filter((_, index) => index !== sourceIndex));
  };

  const moveObservationSource = async (sourceIndex, direction) => {
    const targetIndex = sourceIndex + direction;
    if (targetIndex < 0 || targetIndex >= activeObservationSources.length) {
      return;
    }
    const reorderedSources = [...activeObservationSources];
    const [moved] = reorderedSources.splice(sourceIndex, 1);
    reorderedSources.splice(targetIndex, 0, moved);
    await saveExternalSources(reorderedSources);
  };

  const openAddDialog = () => {
    setSourceDialog({
      mode: 'add',
      source: buildObservationSource({ id: createObservationSourceId() }),
    });
  };

  const openEditDialog = (sourceIndex) => {
    setSourceDialog({
      mode: 'edit',
      index: sourceIndex,
      source: buildObservationSource(activeObservationSources[sourceIndex]),
    });
  };

  const closeSourceDialog = () => {
    setSourceDialog(null);
  };

  const confirmSourceDialog = async (source) => {
    if (!sourceDialog) {
      return false;
    }

    const nextSources = sourceDialog.mode === 'add'
      ? [...activeObservationSources, source]
      : activeObservationSources.map((item, index) => (
        index === sourceDialog.index ? source : item
      ));

    const success = await saveExternalSources(nextSources);
    if (success) {
      closeSourceDialog();
    }
    return success;
  };

  if (!app) {
    return null;
  }

  const dialogSource = sourceDialog?.source;

  return (
    <>
      <Stack horizontal tokens={StackTokens.spacing}>
        <CommandButton
          text="Add data source"
          iconProps={{ iconName: 'Add' }}
          className={[Theme.styles.outlineControlButton, Theme.styles.commandButton].join(' ')}
          disabled={!externalSourcesEnabled}
          onClick={openAddDialog}
        />
      </Stack>
      <Separator styles={Theme.styleObjects.separatorSmallMargin} />
      <Text block style={{ margin: '10px 0 0 0' }}>
        You can connect your app to an external case data source here.
        <HelpButton styles={{ root: { marginLeft: '5px' } }} title="Data sources">
          <div style={isMobile ? {} : { maxWidth: '45vw' }}>
            {
              TextRendererHelper.renderPreformattedBlocks([
                'If configured, when a calculation is triggered in form view, case data will be loaded and applied from these sources before any user input is applied.',
                'If case data is provided for the same variable is in multiple sources, the data from the last source in the list will be used.',
                'If case data for a variable was loaded from a source, and also provided by the user, the user data will be used.',
                'The format of data at the source must be a JSON array with the following structure:',
                '<pre>',
                '[',
                '  {',
                '    "node": "nodeId",',
                '    "network": "networkId",',
                '    "entries": [',
                '      {',
                '        "value": "string",',
                '        "weight": "number",',
                '      },',
                '    ],',
                '  }',
                '  ...',
                ']',
                '</pre>',
                'Weights of entries will be normalized to sum to 1.',
                'Alternatively, instead of `entries` array, a simple single `value` string can be provided.',
                'The request will be made as a GET request.',
              ])
            }
          </div>
        </HelpButton>
      </Text>

      <Checkbox
        label="Enable external case data sources"
        checked={externalSourcesEnabled}
        onChange={async (ev, checked) => {
          await saveExternalSources(activeObservationSources, !!checked);
        }}
      />

      {externalSourcesEnabled && (
        <Stack vertical tokens={StackTokens.spacing5} style={{ marginTop: '10px' }}>
          {activeObservationSources.map((observationSource, index) => {
            const iconSrc = dataSourceIconSrc(observationSource.icon);
            return (
              <Stack
                key={observationSource.id}
                horizontal
                verticalAlign="center"
                tokens={StackTokens.spacing5}
                className={componentStyles.sourceListRow}
              >
                <Stack
                  horizontal
                  verticalAlign="center"
                  className={componentStyles.sourceRowActions}
                >
                  <IconButton
                    iconProps={{ iconName: 'ChevronUp' }}
                    styles={reorderIconButtonStyles}
                    disabled={index === 0}
                    onClick={() => moveObservationSource(index, -1)}
                  />
                  <IconButton
                    iconProps={{ iconName: 'ChevronDown' }}
                    styles={reorderIconButtonStyles}
                    disabled={index === activeObservationSources.length - 1}
                    onClick={() => moveObservationSource(index, 1)}
                  />
                  <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => removeObservationSource(index)}
                  />
                </Stack>
                {iconSrc ? (
                  <img
                    alt=""
                    src={iconSrc}
                    width={DATA_SOURCE_ICON_MENU_PX}
                    height={DATA_SOURCE_ICON_MENU_PX}
                    className={componentStyles.sourceIcon}
                  />
                ) : null}
                <LinkButton
                  text={(observationSource.name || '').trim() || `Source ${index + 1}`}
                  onClick={() => openEditDialog(index)}
                />
              </Stack>
            );
          })}
        </Stack>
      )}

      <ObservationSourceDialog
        isOpen={!!sourceDialog}
        title={sourceDialog?.mode === 'add' ? 'Add data source' : 'Edit data source'}
        confirmText={sourceDialog?.mode === 'add' ? 'Add' : 'Save'}
        source={dialogSource}
        onDismiss={closeSourceDialog}
        onConfirm={confirmSourceDialog}
      />
    </>
  );
}
